import {useEffect, useRef, useState} from 'react';

/**
 * Reactive handle to a debounced semantic-search query.
 *
 * `hits` is `null` when no search is active (UI should render the full list).
 * When a query runs, it resolves to a `Map<key, score>` so callers can filter
 * and sort their own data set without re-fetching. On HTTP 503 (Ollama down)
 * `unavailable` becomes true and `hits` is cleared.
 */
export interface SemanticSearch {
    query: string;
    setQuery: (value: string) => void;
    hits: Map<string, number> | null;
    loading: boolean;
    unavailable: boolean;
    error: boolean;
}

interface Options<T> {
    searchFn: (query: string) => Promise<T[]>;
    extractKey: (hit: T) => { key: string | undefined; score: number | undefined };
    /** Delay after the last keystroke before the query is sent. Defaults to 700ms. */
    debounceMs?: number;
    /**
     * Minimum trimmed query length before a request is fired. Shorter queries
     * are treated as "not yet searching" (full list stays visible, no spinner).
     * Defaults to 3.
     */
    minLength?: number;
}

/**
 * Debounced semantic-search hook. Keeps stable callbacks in refs so effect only
 * re-runs when the query string changes.
 */
export function useSemanticSearch<T>({
    searchFn,
    extractKey,
    debounceMs = 700,
    minLength = 3,
}: Options<T>): SemanticSearch {
    const [query, setQuery] = useState('');
    const [hits, setHits] = useState<Map<string, number> | null>(null);
    const [loading, setLoading] = useState(false);
    const [unavailable, setUnavailable] = useState(false);
    const [error, setError] = useState(false);

    const searchFnRef = useRef(searchFn);
    const extractKeyRef = useRef(extractKey);
    searchFnRef.current = searchFn;
    extractKeyRef.current = extractKey;

    useEffect(() => {
        const trimmed = query.trim();
        if (trimmed.length < minLength) {
            setHits(null);
            setLoading(false);
            setUnavailable(false);
            setError(false);
            return;
        }

        setLoading(true);
        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                const results = await searchFnRef.current(trimmed);
                if (cancelled) return;
                const map = new Map<string, number>();
                results.forEach(hit => {
                    const {key, score} = extractKeyRef.current(hit);
                    if (key) map.set(key, score ?? 0);
                });
                setHits(map);
                setUnavailable(false);
                setError(false);
            } catch (e: any) {
                if (cancelled) return;
                const status = e?.response?.status ?? e?.status;
                setUnavailable(status === 503);
                setError(status !== 503);
                setHits(new Map());
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, debounceMs);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [query, debounceMs, minLength]);

    return {query, setQuery, hits, loading, unavailable, error};
}
